/**
 * byo-v4-rail.js — the progress rail, stage gating and the primary button's
 * label and hint (P4).
 *
 * Ported by name from `byo-stages-v3.jsx`: `nextFor` (line 4), `prevStage` /
 * `nextStage` / `stepDone` / `canGo` (10-14), `gateReason` (15), `StageHead`
 * (18), `stepProgress` (188), `stepStatus` (194) and `ProgressRail` (201).
 * DESIGN-SPEC.md §3.5.
 *
 * One deviation, recorded as DEVIATIONS.md D28: the design's `stepProgress`
 * fills the Fill segment on `slotsUsed / minimum` with no items-mode branch,
 * while `stepStatus` right beneath it does branch. On an items-mode build that
 * puts a bar at 3/5 above a line reading "2 of 4 items". DESIGN-SPEC.md §3.5
 * says "Fill = items toward the minimum", so items mode counts items.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html } from './byo-v4-vendor.js';
const { Icon } = (window.BYOV4 = window.BYOV4 || {});
/* Counts as words (D70), read at call time from byo-v4-core.js. */
const w = (n) => (window.BYO && window.BYO.words ? window.BYO.words(n) : String(n));
const W = (n) => (window.BYO && window.BYO.Words ? window.BYO.Words(n) : String(n));
/** byo-core-v2.jsx:9-10. */
export const STAGES = [
  { id: 'fill', label: 'Fill' },
  { id: 'personal', label: 'Personalise' },
  { id: 'review', label: 'Wrap' },
];

export const STAGE_TITLES = {
  fill: 'Fill Your Box',
  personal: 'Personalise It',
  wrap: 'Choose Your Box',
  review: 'Wrap It Up',
};

export const prevStage = (s) => STAGES[Math.max(0, STAGES.findIndex((x) => x.id === s) - 1)].id;
export const nextStage = (s) => STAGES[Math.min(STAGES.length - 1, STAGES.findIndex((x) => x.id === s) + 1)].id;

/**
 * Gating — byo-stages-v3.jsx:13. Back is always allowed; forward is one step at
 * a time and only when the current step is complete, so nobody skips
 * Personalise.
 */
/* Personalise is done when its touches are. The keepsake box and its wrap are
   chosen on Wrap It Up (D70), so a missing wrap blocks Add To Cart there, not
   the way back into Wrap. */
export const stepDone = (b, s) => (s === 'fill' ? b.meetsMin : s === 'personal' ? b.touchesOk : b.canSubmit);

export function canGo(b, from, to) {
  const i = STAGES.findIndex((s) => s.id === from);
  const j = STAGES.findIndex((s) => s.id === to);
  if (j <= i) return true;
  if (j > i + 1) return false;
  return stepDone(b, from);
}

/** Why a step cannot be reached yet, as one short line for the toast. */
export function gateReason(b, from) {
  if (from === 'fill') {
    return b.itemsMode
      ? `Add ${w(b.need)} more item${b.need > 1 ? 's' : ''} to continue`
      : `Add ${window.BYO.fmtSlots(b.need)} more space${b.need > 1 ? 's' : ''} to continue`;
  }
  if (from === 'personal') {
    if (!b.photoOk) return 'Add your photo, or switch Photo off';
    if (!b.songOk) return 'Tell us the artist and song, or switch Song off';
    if (!b.dateOk) return 'Pick the day it should be opened';
    return 'Write your gift message, or switch it off';
  }
  return 'Finish the earlier steps first';
}

/** stepProgress — byo-stages-v3.jsx:188, with the items-mode branch of D28. */
export function stepProgress(b, id) {
  if (id === 'fill') {
    return b.itemsMode
      ? Math.min(1, b.minItems ? b.itemCount / b.minItems : 0)
      : Math.min(1, b.minimum ? b.slotsUsed / b.minimum : 0);
  }
  if (id === 'wrap') return b.wrapOk ? 1 : 0.5;
  if (id === 'personal') return b.touchesOk ? 1 : 0.5;
  return b.canSubmit ? 1 : 0;
}

/** stepStatus — byo-stages-v3.jsx:194. The line under the rail. */
export function stepStatus(b, id) {
  const fmt = window.BYO.fmtSlots;

  if (id === 'fill' && b.itemsMode) {
    return b.meetsMin
      ? b.remaining > 1e-9
        ? `${W(b.itemCount)} items in · keep adding while it fits`
        : `${W(b.itemCount)} items in · box full`
      : b.itemCount
        ? `${W(b.itemCount)} of ${w(b.minItems)} items · add ${w(b.need)} more to check out`
        : `No items yet · add ${w(b.need)} to check out`;
  }
  if (id === 'fill') {
    return b.meetsMin
      ? b.remaining > 1e-9
        ? `${fmt(b.slotsUsed)} of ${b.capacity} spaces filled · room for ${fmt(b.remaining)} more`
        : 'Box full · ready for the next step'
      : `${fmt(b.slotsUsed)} of ${fmt(b.minimum)} spaces needed to check out`;
  }
  if (id === 'personal') {
    const box = b.box;
    const n = [box.photo && box.photo.on, box.song && box.song.on, box.date && box.date.on, !!box.message].filter(
      Boolean
    ).length;
    if (!b.touchesOk) return 'Finish the touch you switched on';
    return n ? `${W(n)} personal touch${n > 1 ? 'es' : ''} added · all optional` : 'All optional · skip if you like';
  }
  return b.canSubmit ? `${b.money(b.total)} · ready to add to cart` : b.blockers.join(' · ');
}

/**
 * nextFor — byo-stages-v3.jsx:4. The primary button's label, disabled state and
 * hint for each stage. DESIGN-SPEC.md §3.5.
 */
export function nextFor(b, stage, config) {
  const money = b.money;
  const fmt = window.BYO.fmtSlots;

  if (stage === 'fill') {
    const hint = !b.box.items.length
      ? `${money(config.base || 0)} covers our gift wrapping service · add what goes inside`
      : !b.meetsMin
        ? b.itemsMode
          ? `Add ${w(b.need)} more item${b.need > 1 ? 's' : ''} to continue`
          : `Add ${fmt(b.need)} more space${b.need > 1 ? 's' : ''} to continue`
        : b.remaining <= 1e-9
          ? /* Nothing: MeterText already carries the black "Full" pill with a
               tick, and the ShippingBar says whether it ships free. Saying
               "Box full" a third time is noise. DEVIATIONS.md D31. */
            ''
          : b.toFree > 0
            ? `${money(b.toFree)} more and delivery's on us`
            : b.itemsMode
              ? 'Ships free · keep adding while it fits'
              : `Ships free · room for ${fmt(b.remaining)} more`;
    return { label: 'Personalise', disabled: false, hint };
  }

  if (stage === 'personal') {
    const hint = !b.photoOk
      ? 'Add your photo before moving on'
      : !b.songOk
        ? 'Tell us the artist and song'
        : !b.dateOk
          ? 'Pick the day it should be opened'
          : b.messageOk === false
            ? 'Write your gift message'
            : 'All optional, all yours';
    return { label: 'Wrap It Up', disabled: false, hint };
  }

  return {
    label: b.editing ? `Update ${b.bundleName || 'This Box'}` : 'Add To Cart',
    disabled: !!b.busy,
    hint: b.blockers.length ? b.blockers.join(' · ') : `${money(b.total)} · ready to go`,
  };
}

/** StageHead — byo-stages-v3.jsx:18. "Step 2 of 3" over the stage title. */
export function StageHead({ stage, onHelp }) {
  const i = STAGES.findIndex((s) => s.id === stage);
  /* D65: the same Help button as beside "Fill Your Box", so help is as easy
     to find on Personalise and Confirm. Hidden on desktop with the Fill one,
     where the bar's Help pill is on every stage. */
  return html`
    <div class="byo-v4-stagehead">
      <div class="byo-v4-stagehead__step">Step ${w(i + 1)} of ${w(STAGES.length)}</div>
      <div class="byo-v4-fill__headrow byo-v4-stagehead__row">
        <h2 class="byo-v4-stagehead__h2">${STAGE_TITLES[stage]}</h2>
        ${onHelp &&
        html`<button type="button" class="byo-v4-fill__help" onClick=${onHelp} aria-label="Help" title="Help">
          <${Icon} name="help" size=${19} />
        </button>`}
      </div>
    </div>
  `;
}

/**
 * ProgressRail — byo-stages-v3.jsx:201. Three equal segments with labels, and a
 * status line beneath. On a phone the current segment shows the stage's title
 * (`titles`) and Help sits at the end (D130, Alan, 11 Oct 2026). A tap on a gated segment does not move; the caller
 * raises `gateReason` as a coral error toast (DESIGN-SPEC.md §3.5).
 */
export function ProgressRail({ b, stage, onGo, status = true, class: cls = '', titles = null, onHelp = null }) {
  const idx = STAGES.findIndex((s) => s.id === stage);

  return html`
    <div class=${`byo-v4-rail ${cls}`.trim()}>
      <div class="byo-v4-rail__track">
        ${STAGES.map((s, i) => {
          const past = i < idx;
          const cur = i === idx;
          const p = past ? 1 : cur ? stepProgress(b, s.id) : 0;
          const done = past || (cur && p >= 1);
          const reachable = past || cur || canGo(b, stage, s.id);

          return html`
            <button
              key=${s.id}
              class=${`byo-v4-rail__seg${cur ? ' is-current' : ''}${done ? ' is-done' : ''}${
                reachable ? '' : ' is-locked'
              }`}
              onClick=${() => onGo && !cur && onGo(s.id)}
              aria-current=${cur ? 'step' : undefined}
              aria-disabled=${!reachable}
            >
              <span class="byo-v4-rail__bar">
                <span
                  class=${`byo-v4-rail__fill${done ? ' is-done' : ''}`}
                  style=${`width:${(p * 100).toFixed(1)}%`}
                ></span>
              </span>
              <span class=${`byo-v4-rail__label${cur && titles && titles[s.id] ? ' byo-v4-rail__label--titled' : ''}`}>
                ${done && !cur && html`<${Icon} name="check" size=${11} stroke=${3} />`}<span class="byo-v4-rail__short">${s.label}</span>${cur &&
                titles &&
                titles[s.id] &&
                html`<span class="byo-v4-rail__title">${titles[s.id]}</span>`}
              </span>
            </button>
          `;
        })}
      </div>

      ${/* D130: on a phone the rail is the stage's title, so Help sits at its end. */ ''}
      ${onHelp &&
      html`<button type="button" class="byo-v4-fill__help byo-v4-rail__help" onClick=${onHelp} aria-label="Help" title="Help">
        <${Icon} name="help" size=${16} />
      </button>`}

      ${status &&
      html`<div class="byo-v4-rail__status">
        <span class=${`byo-v4-rail__dot${stepProgress(b, stage) >= 1 ? ' is-done' : ''}`}></span>
        <span class="byo-v4-rail__statustext">${stepStatus(b, stage)}</span>
      </div>`}
    </div>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { ProgressRail, STAGES, STAGE_TITLES, StageHead, canGo, gateReason, nextFor, nextStage, prevStage, stepDone, stepProgress, stepStatus });
